import { useState, useEffect, useRef, useCallback } from 'react';
import { getToken } from '../utils/auth';

const MAX_LINES = 10_000;

// Derive the API base URL from REACT_APP_API_URL (strip /graphql suffix)
function getApiBase() {
  const env = window._env_ || {};
  const apiUrl = env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '/graphql';
  return apiUrl.replace(/\/graphql\/?$/, '');
}

/**
 * Custom hook for streaming live build logs via SSE.
 *
 * @param {string} workflowName
 * @param {string|null} stepName - node ID to stream logs for
 * @param {{ enabled: boolean }} options
 * @returns {{ text: string, status: string, lineCount: number, reconnect: () => void }}
 */
export function useLogStream(workflowName, stepName, { enabled = true } = {}) {
  const [status, setStatus] = useState('idle'); // idle | connecting | streaming | complete | fallback
  const [lineCount, setLineCount] = useState(0);
  const [text, setText] = useState('');
  const linesRef = useRef([]);
  const abortRef = useRef(null);
  const reconnectKey = useRef(0);
  const [trigger, setTrigger] = useState(0);

  const reconnect = useCallback(() => {
    reconnectKey.current++;
    linesRef.current = [];
    setText('');
    setLineCount(0);
    setStatus('idle');
    setTrigger((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !workflowName || !stepName) {
      setStatus('idle');
      return;
    }

    const abortController = new AbortController();
    abortRef.current = abortController;

    setStatus('connecting');

    const apiBase = getApiBase();
    const url = `${apiBase}/api/v1/workflows/${encodeURIComponent(workflowName)}/logs/stream?stepName=${encodeURIComponent(stepName)}`;

    const token = getToken();

    async function startStream() {
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            Accept: 'text/event-stream',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          setStatus('fallback');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const eventBlock of events) {
            if (!eventBlock.trim() || eventBlock.startsWith(':')) continue;

            let eventType = 'message';
            let data = '';

            for (const line of eventBlock.split('\n')) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                data += line.slice(5).trim();
              }
            }

            if (eventType === 'connected') {
              setStatus('streaming');
            } else if (eventType === 'log') {
              try {
                const parsed = JSON.parse(data);
                const raw = parsed.content || '';
                const content = raw.endsWith('\n') ? raw : raw + '\n';
                const lines = linesRef.current;
                lines.push(content);
                // Bounded buffer: drop oldest lines if over limit
                if (lines.length > MAX_LINES) {
                  const excess = lines.length - MAX_LINES;
                  lines.splice(0, excess);
                }
                setLineCount(lines.length);
                setText(lines.join(''));
              } catch {
                // Ignore malformed data
              }
            } else if (eventType === 'complete') {
              setStatus('complete');
            } else if (eventType === 'fallback') {
              setStatus('fallback');
            }
          }
        }

        // Stream ended without explicit complete/fallback event
        setStatus((prev) => (prev === 'streaming' ? 'complete' : prev));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setStatus('fallback');
      }
    }

    startStream();

    return () => {
      abortController.abort();
    };
  }, [workflowName, stepName, enabled, trigger]);

  return { text, status, lineCount, reconnect };
}
